"use client"

import { useRef } from "react"
import { Loader2, Paperclip } from "lucide-react"
import { cn } from "@/lib/utils"
import { useIsMobile } from "@/hooks/use-mobile"
import { useDrawerKeyboardStyle } from "@/hooks/use-drawer-keyboard-style"
import { Button } from "@/components/ui/button"
import { VoiceTextarea } from "@/components/voice"
import { Toggle } from "@/components/ui/toggle"
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Drawer, DrawerContent, DrawerDescription, DrawerFooter, DrawerHeader, DrawerTitle } from "@/components/ui/drawer"
import { UploadPreviews, useFileDrop, type AttachmentUploads } from "@/components/bugfix-agent/attachments"
import { COPILOT_TAGS, MAX_COMMENT, PRODUCT_TAGS, SNAPSHOT_MESSAGES } from "@/modules/feedback/lib/shared/types"
import { CaptureButtons } from "./capture-buttons"
import { useScreenCapture, type FeedbackDraft } from "./feedback-provider"
import { plural } from "./labels"

export interface FeedbackDialogProps {
  open: boolean
  draft: FeedbackDraft
  onDraft: React.Dispatch<React.SetStateAction<FeedbackDraft>>
  uploads: AttachmentUploads
  sending: boolean
  onClose: () => void
  onSubmit: () => void
}

const titleOf = (draft: FeedbackDraft) => (draft.kind === "product" ? "Обратная связь" : "Поделиться отзывом")

function descriptionOf(draft: FeedbackDraft) {
  if (draft.kind === "product") return "Что сломалось, мешает или чего не хватает"
  return draft.rating === "down" ? "Что не так с ответом" : "Что понравилось в ответе"
}

/** Пока выезжает клавиатура, шторка ужимается — скроллим поле после этого, иначе оно уйдёт под клавиатуру */
function scrollFocusedFieldIntoView(e: React.FocusEvent<HTMLDivElement>) {
  const t = e.target
  if (t.tagName !== "TEXTAREA" && t.tagName !== "INPUT") return
  setTimeout(() => {
    if (document.activeElement === t) t.scrollIntoView({ behavior: "smooth", block: "nearest" })
  }, 350)
}

export function FeedbackDialog(props: FeedbackDialogProps) {
  const { open, draft, onClose } = props
  const isMobile = useIsMobile()
  const keyboardStyle = useDrawerKeyboardStyle(open && isMobile)
  const onOpenChange = (v: boolean) => !v && onClose()

  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={onOpenChange} repositionInputs={false}>
        <DrawerContent className="max-h-[92dvh]" style={keyboardStyle} data-testid="feedback-dialog">
          <DrawerHeader>
            <DrawerTitle>{titleOf(draft)}</DrawerTitle>
            <DrawerDescription className="sr-only">{descriptionOf(draft)}</DrawerDescription>
          </DrawerHeader>
          <div className="min-h-0 flex-1 overflow-y-auto px-4" onFocus={scrollFocusedFieldIntoView}>
            <FeedbackForm {...props} />
          </div>
          <DrawerFooter>
            <SubmitButton {...props} />
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] gap-5 overflow-y-auto sm:max-w-xl" data-testid="feedback-dialog" onOpenAutoFocus={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle>{titleOf(draft)}</DialogTitle>
          <DialogDescription className="sr-only">{descriptionOf(draft)}</DialogDescription>
        </DialogHeader>
        <FeedbackForm {...props} />
        <DialogFooter>
          <SubmitButton {...props} />
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function FeedbackForm({ draft, onDraft, uploads }: FeedbackDialogProps) {
  const drop = useFileDrop(uploads.add)
  const input = useRef<HTMLInputElement>(null)
  const canCapture = !!useScreenCapture()?.mode

  const tags = draft.kind === "product" ? PRODUCT_TAGS : draft.rating ? COPILOT_TAGS[draft.rating] : []
  const toggleTag = (t: string, on: boolean) => onDraft((d) => ({ ...d, tags: on ? [...d.tags, t] : d.tags.filter((x) => x !== t) }))
  const full = uploads.left <= 0

  const messages = draft.target ? Math.min(SNAPSHOT_MESSAGES, Math.max(1, draft.target.getMessages().length)) : 0
  const path = draft.context?.path ?? (typeof window !== "undefined" ? window.location.pathname : "")

  return (
    <div {...drop.props} className={cn("flex flex-col gap-4 rounded-md", drop.over && "ring-ring/50 ring-[3px] ring-offset-4")}>
      {tags.length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid="feedback-tags">
          {tags.map((t) => (
            <Toggle
              key={t}
              variant="outline"
              pressed={draft.tags.includes(t)}
              onPressedChange={(on) => toggleTag(t, on)}
              className="rounded-full px-3.5 font-normal"
            >
              {t}
            </Toggle>
          ))}
        </div>
      )}

      <VoiceTextarea
        tone="default"
        value={draft.comment}
        onChange={(e) => onDraft((d) => ({ ...d, comment: e.target.value.slice(0, MAX_COMMENT) }))}
        onVoiceInsert={(next) => onDraft((d) => ({ ...d, comment: next.slice(0, MAX_COMMENT) }))}
        placeholder={draft.kind === "product" ? "Опишите, что произошло и что вы ожидали увидеть" : "Поделитесь подробностями (необязательно)"}
        className="min-h-24 resize-y"
        data-testid="feedback-comment"
      />

      <div className="flex flex-col gap-2">
        <UploadPreviews uploads={uploads} />
        <div className="-ml-2 flex flex-wrap items-center gap-1">
          <input
            ref={input}
            type="file"
            multiple
            className="hidden"
            onChange={(e) => {
              uploads.add(e.target.files)
              e.target.value = ""
            }}
          />
          <Button type="button" variant="ghost" size="sm" className="text-muted-foreground" onClick={() => input.current?.click()} disabled={full} data-testid="feedback-attach">
            <Paperclip />
            Прикрепить
          </Button>
          <CaptureButtons add={uploads.add} disabled={full} />
          {uploads.items.length > 0 && (
            <span className="text-muted-foreground ml-1 text-xs">
              {uploads.items.length} из {uploads.max}
            </span>
          )}
        </div>
      </div>

      <p className="text-muted-foreground text-xs" data-testid="feedback-context-note">
        {draft.kind === "copilot"
          ? `Команда увидит последние ${messages} ${plural(messages, "сообщение", "сообщения", "сообщений")} диалога, страницу и устройство.`
          : `Команда увидит страницу ${path || "/"}, устройство и версию приложения.`}
        {!canCapture && " Чтобы показать проблему на видео, запишите экран средствами телефона и прикрепите ролик."}
      </p>
    </div>
  )
}

function SubmitButton({ draft, uploads, sending, onSubmit }: FeedbackDialogProps) {
  const empty = !draft.comment.trim() && !draft.tags.length && !uploads.items.length
  return (
    <Button type="button" onClick={onSubmit} disabled={sending || uploads.uploading || empty} data-testid="feedback-submit">
      {(sending || uploads.uploading) && <Loader2 className="animate-spin" />}
      {uploads.uploading ? "Загрузка файлов…" : "Отправить"}
    </Button>
  )
}
